import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { get, post, fileUrl } from "@/lib/api";
import { PageLoader, PageHeader, EmptyState, Badge, statusTone } from "@/components/common";
import { ShieldCheck, Eye, X, CheckCircle2, Loader2 } from "lucide-react";

export default function Approvals() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("pending");
  const [reason, setReason] = useState({});
  const [previewId, setPreviewId] = useState(null);
  const { data, isLoading } = useQuery({ queryKey: ["approvals", status], queryFn: () => get(`/approvals?status=${status}`) });
  const review = async (id, decision) => { await post(`/approvals/${id}/review`, { decision, reason: reason[id] || "" }); toast.success(`Content ${decision === "approve" ? "approved & published" : "rejected"}`); setPreviewId(null); qc.invalidateQueries({ queryKey: ["approvals"] }); };

  return (
    <div>
      <PageHeader title="Content Approval Queue" subtitle="Verify every submission — view all questions/content, then approve or reject" />
      <div className="flex gap-2 mb-5">
        {[["pending", "Pending"], ["approved", "Approved"], ["rejected", "Rejected"]].map(([k, l]) => (
          <button key={k} onClick={() => setStatus(k)} data-testid={`approval-filter-${k}`} className={`px-4 py-2 rounded-lg text-sm font-semibold ${status === k ? "bg-indigo-800 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{l}</button>
        ))}
      </div>
      {isLoading ? <PageLoader /> : !data?.length ? <EmptyState icon={ShieldCheck} title="Queue is clear" hint="No items in this status." /> : (
        <div className="space-y-3">{data.map((a) => (
          <div key={a.id} className="bg-white rounded-2xl border border-slate-200 p-4" data-testid={`approval-${a.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div><p className="font-semibold text-slate-800">{a.title}</p><p className="text-xs text-slate-500">{a.content_type} · by {a.submitted_by_name} · {new Date(a.submitted_at).toLocaleString()}</p></div>
              <div className="flex items-center gap-2">
                <button onClick={() => setPreviewId(a.id)} data-testid={`view-approval-${a.id}`} className="text-indigo-700 text-sm font-semibold flex items-center gap-1"><Eye className="h-4 w-4" /> View & Verify</button>
                <Badge tone={statusTone(a.status)}>{a.status}</Badge>
              </div>
            </div>
            {a.status === "pending" && (
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <input value={reason[a.id] || ""} onChange={(e) => setReason({ ...reason, [a.id]: e.target.value })} placeholder="Rejection reason (optional)" data-testid={`reason-${a.id}`} className="flex-1 min-w-[180px] border border-slate-200 rounded-lg px-3 py-2 text-sm" />
                <button onClick={() => review(a.id, "approve")} data-testid={`approve-${a.id}`} className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold">Approve</button>
                <button onClick={() => review(a.id, "reject")} data-testid={`reject-${a.id}`} className="px-4 py-2 rounded-lg border border-red-200 text-red-600 text-sm font-semibold">Reject</button>
              </div>
            )}
            {a.reason && a.status === "rejected" && <p className="text-sm text-red-600 mt-2">Reason: {a.reason}</p>}
          </div>))}</div>
      )}
      {previewId && <PreviewModal aid={previewId} onClose={() => setPreviewId(null)} onReview={review} reason={reason} setReason={setReason} />}
    </div>
  );
}

function PreviewModal({ aid, onClose, onReview, reason, setReason }) {
  const [p, setP] = useState(null);
  useEffect(() => { get(`/approvals/${aid}/preview`).then(setP).catch(() => toast.error("Could not load")); }, [aid]);
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl p-6 max-w-3xl w-full my-6">
        {!p ? <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-indigo-700" /></div> : (
          <>
            <div className="flex items-start justify-between mb-3">
              <div><h3 className="font-display font-bold text-xl text-slate-900">{p.approval.title}</h3><p className="text-xs text-slate-500">{p.type} · by {p.approval.submitted_by_name}</p></div>
              <button onClick={onClose}><X className="h-5 w-5" /></button>
            </div>

            {p.type === "test" && (
              <div>
                <div className="flex flex-wrap gap-2 text-xs mb-3">
                  <Badge tone="indigo">{p.test?.test_type}</Badge><Badge tone="slate">{p.test?.total_questions} Qs</Badge><Badge tone="slate">{p.test?.total_marks} marks</Badge><Badge tone="slate">{p.test?.duration_min} min</Badge>{p.test?.is_free ? <Badge tone="green">Free</Badge> : <Badge tone="amber">₹{p.test?.price}</Badge>}
                </div>
                {(p.sections || []).map((s, si) => (
                  <div key={si} className="mb-4">
                    <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2">{s.name} · {s.questions.length} questions</p>
                    {s.questions.map((q, i) => (
                      <div key={q.id} className="border border-slate-200 rounded-xl p-3 mb-2" data-testid={`preview-q-${i}`}>
                        <p className="font-medium text-slate-800">Q{i + 1}. {q.question_text}</p>
                        {q.question_image && <img src={fileUrl(q.question_image.replace("/api", ""))} alt="" className="max-h-36 rounded-lg border mt-2" />}
                        <div className="mt-2 space-y-1">{(q.options || []).map((o) => (
                          <p key={o.key} className={`text-sm px-2 py-1 rounded flex items-center gap-2 ${q.correct_answer?.includes(o.key) ? "bg-emerald-50 text-emerald-800" : "text-slate-600"}`}><span className="font-bold">{o.key}.</span> {o.text}{q.correct_answer?.includes(o.key) && <CheckCircle2 className="h-3.5 w-3.5 ml-auto" />}</p>))}</div>
                        {q.explanation && <p className="text-xs text-slate-500 mt-2">💡 {q.explanation}</p>}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}

            {p.type === "course" && (
              <div>
                <p className="text-sm text-slate-600 mb-3">{p.course?.description}</p>
                {(p.modules || []).map((m) => (
                  <div key={m.id} className="mb-2"><p className="font-semibold text-slate-800">{m.title}</p>
                    {m.lessons.map((l) => <p key={l.id} className="text-sm text-slate-500 ml-3">• {l.title} ({l.lesson_type})</p>)}
                  </div>
                ))}
              </div>
            )}

            {p.type === "question" && p.question && (
              <div className="border border-slate-200 rounded-xl p-3">
                <p className="font-medium text-slate-800">{p.question.question_text}</p>
                <div className="mt-2 space-y-1">{(p.question.options || []).map((o) => <p key={o.key} className={`text-sm px-2 py-1 rounded ${p.question.correct_answer?.includes(o.key) ? "bg-emerald-50 text-emerald-800" : "text-slate-600"}`}>{o.key}. {o.text}</p>)}</div>
              </div>
            )}

            {p.approval.status === "pending" && (
              <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4 mt-4">
                <input value={reason[aid] || ""} onChange={(e) => setReason({ ...reason, [aid]: e.target.value })} placeholder="Rejection reason (optional)" className="flex-1 min-w-[180px] border border-slate-200 rounded-lg px-3 py-2 text-sm" />
                <button onClick={() => onReview(aid, "approve")} data-testid="preview-approve-btn" className="px-5 py-2 rounded-lg bg-emerald-600 text-white text-sm font-bold">Approve & Publish</button>
                <button onClick={() => onReview(aid, "reject")} data-testid="preview-reject-btn" className="px-5 py-2 rounded-lg border border-red-200 text-red-600 text-sm font-bold">Reject</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
